import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import type { WeekData, WeekFilterState } from '../types/weekOverview';
import {
  buildUnplannedWeekData,
  mapSchedulingResultToWeekData
} from '../services/schedulingResultService';
import { fetchWeekSchedulingResult } from '../services/weekSchedulingResultService';
import type { WeekResultOutcome } from '../services/weekSchedulingResultService';
import { useLocations } from './useLocations';
import { useStaffCompetences } from './useStaffCompetences';

// The location and scenario are captured at open time. If either changes while
// the modal is open (year switch, history navigation) the open week is stale:
// it stops rendering immediately and is then discarded, so it can never
// re-appear when the page returns to the original location and scenario.
interface OpenWeek {
  year: number;
  weekNumber: number;
  locationId: string;
  scenarioId: string;
}

export interface UseWeekCalendarModalResult {
  weekData: WeekData | null;
  isLoading: boolean;
  filters: WeekFilterState;
  setFilters: (filters: WeekFilterState) => void;
  openCalendar: (year: number, weekNumber: number) => void;
  closeCalendar: () => void;
}

const EMPTY_FILTERS: WeekFilterState = {
  roomId: null,
  staffId: null,
  patientId: null
};

// The fetch outcome, remembered with the week it was fetched for so a stale
// response never renders under another week's header.
interface LoadedResult {
  year: number;
  weekNumber: number;
  outcome: WeekResultOutcome;
}

// Backs the calendar modal when it is opened for one fixed week (from the year
// view) instead of through the week overview's own navigation. Mirrors
// useWeekOverview's data resolution, but reuses the year view's already
// resolved scenario: weeks without a completed calculation get the unplanned
// shell. weekData stays null until the fetch settles; isLoading covers the
// window in between so the page can show a loading shell.
export function useWeekCalendarModal(
  locationId: string | undefined,
  scenarioId: string | undefined
): UseWeekCalendarModalResult {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { locationsById } = useLocations();
  const { competences } = useStaffCompetences();

  const [openWeek, setOpenWeek] = useState<OpenWeek | null>(null);
  const [filters, setFilters] = useState<WeekFilterState>(EMPTY_FILTERS);
  const [loadedResult, setLoadedResult] = useState<LoadedResult | null>(null);

  // Monotonic fetch counter: only the newest request may write state, so
  // opening another week can never let an older response overtake a newer one.
  const fetchSeqRef = useRef(0);

  const closeCalendar = useCallback(() => {
    fetchSeqRef.current++;
    setOpenWeek(null);
    setLoadedResult(null);
  }, []);

  useEffect(() => {
    // Bumped even when there is nothing to fetch: discarding the open week (a
    // close, or the stale-week discard below) re-runs this effect, and that bump
    // is what stops the abandoned request from writing state or raising a dialog
    // over whatever now owns the screen.
    const seq = ++fetchSeqRef.current;
    if (!openWeek) return;
    const { year, weekNumber, locationId: fetchLocationId, scenarioId: fetchScenarioId } = openWeek;
    // Unmounting does not re-run this effect, so the sequence counter alone
    // cannot stop a late failure from raising a dialog over the next page.
    let isActive = true;

    // The service logs every failure before this runs, so a response the user has
    // moved on from still leaves a trace even though it raises no dialog.
    void fetchWeekSchedulingResult({
      locationId: fetchLocationId,
      scenarioId: fetchScenarioId,
      weekNumber
    }).then((outcome) => {
      if (!isActive || fetchSeqRef.current !== seq) return;
      // A failure closes the modal rather than showing the unplanned state,
      // which would claim the week has no plan when we simply don't know.
      if (outcome.kind === 'failed') {
        setOpenWeek(null);
        setLoadedResult(null);
        dispatch(showError({ message: t('fetch-error') }));
        return;
      }
      setLoadedResult({ year, weekNumber, outcome });
    });

    return () => {
      isActive = false;
    };
  }, [openWeek, dispatch, t]);

  const locationName = locationId ? (locationsById[locationId]?.name ?? '') : '';

  // An open week belonging to another location or scenario is stale — it reads
  // as closed here, and the discard below then drops it from state.
  const isOpenWeekCurrent =
    openWeek !== null && openWeek.locationId === locationId && openWeek.scenarioId === scenarioId;

  // Hiding the stale week is not enough: keeping it in state let it resurrect
  // once the page came back to the captured location and scenario — the year
  // view mirrors the year into the URL, so browser back/forward alone reopened
  // the modal with a result nobody re-requested. Discard it during render
  // instead (React's adjust-state-on-prop-change pattern) so no committed
  // render ever holds a week the page has moved away from.
  if (openWeek !== null && !isOpenWeekCurrent) {
    setOpenWeek(null);
    setLoadedResult(null);
  }

  const weekData = useMemo<WeekData | null>(() => {
    if (!openWeek || !isOpenWeekCurrent) return null;
    if (
      loadedResult?.year !== openWeek.year ||
      loadedResult.weekNumber !== openWeek.weekNumber
    ) {
      return null;
    }
    if (loadedResult.outcome.kind === 'planned') {
      return mapSchedulingResultToWeekData(
        loadedResult.outcome.response,
        openWeek.year,
        openWeek.weekNumber,
        locationName,
        competences
      );
    }
    return buildUnplannedWeekData(openWeek.year, openWeek.weekNumber, locationName);
  }, [openWeek, isOpenWeekCurrent, loadedResult, locationName, competences]);

  // Opening always starts from a clean slate: filters left over from an
  // earlier visit would silently pre-filter the calendar.
  const openCalendar = useCallback(
    (year: number, weekNumber: number) => {
      if (!locationId || !scenarioId) return;
      setFilters(EMPTY_FILTERS);
      setLoadedResult(null);
      setOpenWeek({ year, weekNumber, locationId, scenarioId });
    },
    [locationId, scenarioId]
  );

  // Open but not yet settled: the page shows a loading shell so the click has
  // immediate feedback and the screen is blocked while the fetch runs.
  const isLoading = isOpenWeekCurrent && weekData === null;

  return { weekData, isLoading, filters, setFilters, openCalendar, closeCalendar };
}
